// UI Controls Layer subcomponent - Composition layer
import type {
  UIControlsLayerAPI,
  UIControlsConfig,
  SketchPaletteToolOptions,
  TransformState,
} from "../../../../types/stage-types.js";
import { createInfoHUD } from "./subcomponents/InfoHUD/InfoHUD.js";
import { createSidebarControls } from "./subcomponents/SidebarControls/SidebarControls.js";
import { createDominantArea } from "./subcomponents/DominantArea/DominantArea.js";
import { createBottomArea } from "./subcomponents/BottomArea/BottomArea.js";
import {
  createSketchToolPalette,
  type SketchToolPaletteAPI,
} from "./subcomponents/SketchToolPalette/SketchToolPalette.js";
import type { VisibilityProfile } from "./visibility-profiles";
import type { HandednessStateAPI } from "@/state/handedness";
import { getModalManager } from "@UI/ModalManager/index.js";

/**
 * Creates a UI Controls Layer for stage interaction controls
 * @param container - Container element for the layer
 * @param config - Layer configuration with event handlers
 * @returns UI Controls Layer API
 */
export const createUIControlsLayer = (
  container: HTMLElement,
  config: UIControlsConfig = {}
): UIControlsLayerAPI => {
  console.log("🔧 UIControlsLayer: Starting creation with config:", config);
  let isDestroyed = false;

  const modalManager = getModalManager();
  // Component instances
  let infoHUD: any;
  let sidebarControls: any;
  let dominantArea: any;
  let bottomArea: any;
  let sketchToolPalette: SketchToolPaletteAPI | null = null;
  let paletteHost: HTMLElement | null = null;
  let unsubscribeHandedness: (() => void) | null = null;
  let layoutRoot: HTMLElement | null = null;
  let currentHandedness: "left" | "right" = "right";
  let isLayerVisible = true;
  let isPaletteAllowed = false;
  let isPaletteOpen = false;
  let paletteAnchorElement: HTMLElement | null = null;
  const paletteOptionsBySubtool: Record<string, SketchPaletteToolOptions> =
    config.sketchPaletteOptionsBySubtool ?? {};

  const closePalette = (): void => {
    if (!isPaletteOpen || !sketchToolPalette) return;
    if (modalManager.getCurrentContent() === sketchToolPalette.getElement()) {
      modalManager.hide();
    }
    isPaletteOpen = false;
    paletteAnchorElement = null;
  };

  const openPalette = (anchorElement: HTMLElement | null): void => {
    if (
      !sketchToolPalette ||
      !anchorElement ||
      !isPaletteAllowed ||
      isDestroyed ||
      !isLayerVisible
    ) {
      return;
    }

    paletteAnchorElement = anchorElement;
    isPaletteOpen = true;

    const anchor =
      currentHandedness === "left" ? "bottom-left" : "bottom-right";
    const offset = {
      x: currentHandedness === "left" ? -12 : 12,
      y: 8,
    };

    modalManager.show({
      content: sketchToolPalette.getElement(),
      anchor,
      anchorElement,
      offset,
      showBackdrop: true,
      closeOnBackdrop: true,
      onClose: () => {
        isPaletteOpen = false;
        paletteAnchorElement = null;
      },
    });
  };

  const togglePaletteForAnchor = (
    anchorElement: HTMLElement | null
  ): void => {
    if (!anchorElement) return;
    if (isPaletteOpen && paletteAnchorElement === anchorElement) {
      closePalette();
      return;
    }
    openPalette(anchorElement);
  };

  const handleSketchToolSettingsRequest = (
    mode: string,
    subtool: string,
    anchor?: HTMLElement | null
  ): void => {
    if (mode !== "sketch") return;
    if (subtool === "delete" || subtool === "delete-clean") {
      config.onSketchDeleteAll?.();
      return;
    }
    if (subtool === "marquee" || subtool === "lasso") {
      config.onSketchSelectAll?.();
      return;
    }
    if (subtool !== "ballpoint" && subtool !== "marker") return;
    if (!anchor) return;
    if (sketchToolPalette) {
      const toolOptions = paletteOptionsBySubtool[subtool];
      const options = toolOptions ?? config.sketchPaletteOptions ?? {};
      sketchToolPalette.setOptions(options);
      const fallbackLabel =
        subtool === "marker"
          ? "Highlighter"
          : subtool === "delete"
            ? "Eraser"
            : "Ballpoint";
      const fallbackVariant =
        subtool === "marker"
          ? "highlighter"
          : subtool === "delete"
            ? "eraser"
            : "ballpoint";
      sketchToolPalette.setTool({
        label: toolOptions?.toolLabel ?? fallbackLabel,
        variant: toolOptions?.toolVariant ?? fallbackVariant,
      });
    }
    togglePaletteForAnchor(anchor);
  };

  /**
   * Initialize UI controls layout structure and create subcomponents
   */
  const initControls = (): void => {
    console.log("🔧 UIControlsLayer: Creating HTML structure");
    // Create main layout structure
    container.innerHTML = `
      <div class="ui-controls-layout">
        <!-- InfoHUD container -->
        <div class="info-hud-container"></div>
        
        <!-- Middle area with sidebar and dominant -->
        <div class="middle-area">
          <div class="sidebar-container"></div>
          <div class="dominant-container"></div>
        </div>
        
        <!-- Bottom area -->
        <div class="bottom-container"></div>
      </div>
    `;

    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }

    layoutRoot = container.querySelector(
      ".ui-controls-layout"
    ) as HTMLElement;

    const handednessState: HandednessStateAPI | null =
      (config.handedness as HandednessStateAPI | null) ??
      (config.appStateManager?.handedness as HandednessStateAPI | null) ??
      null;

    const setLayoutHandedness = (value: "left" | "right") => {
      currentHandedness = value;
      if (!layoutRoot) return;
      layoutRoot.dataset.handedness = value;
      layoutRoot.classList.toggle("is-left-handed", value === "left");
      layoutRoot.classList.toggle("is-right-handed", value !== "left");
    };

    setLayoutHandedness(currentHandedness);

    // Get container elements
    const infoHUDContainer = container.querySelector(
      ".info-hud-container"
    ) as HTMLElement;
    const sidebarContainer = container.querySelector(
      ".sidebar-container"
    ) as HTMLElement;
    const dominantContainer = container.querySelector(
      ".dominant-container"
    ) as HTMLElement;
    const bottomContainer = container.querySelector(
      ".bottom-container"
    ) as HTMLElement;

    // Create subcomponents
    if (infoHUDContainer) {
      const infoHUDConfig: any = {
        initialZoom: 100,
      };
      if (config.onZoomIn) infoHUDConfig.onZoomIn = config.onZoomIn;
      if (config.onZoomOut) infoHUDConfig.onZoomOut = config.onZoomOut;
      if (config.onReset) infoHUDConfig.onReset = config.onReset;
      if (config.onToggleIconModal)
      infoHUDConfig.onToggleIconModal = config.onToggleIconModal;
      if (config.appStateManager)
        infoHUDConfig.appStateManager = config.appStateManager;
      if (handednessState) infoHUDConfig.handedness = handednessState;

      console.log(
        "UIControlsLayer: Creating InfoHUD with config:",
        infoHUDConfig
      );
      infoHUD = createInfoHUD(infoHUDContainer, infoHUDConfig);
    }

    if (sidebarContainer) {
      const sidebarConfig: any = {
        initialTool: "select",
      };
      if (config.onUndo) sidebarConfig.onUndo = config.onUndo;
      if (config.onRedo) sidebarConfig.onRedo = config.onRedo;
      if (config.onModifierChange)
        sidebarConfig.onModifierChange = config.onModifierChange;
      if (config.appStateManager?.uiState)
        sidebarConfig.uiState = config.appStateManager.uiState;
      if (config.appStateManager?.appContext?.eventBus)
        sidebarConfig.eventBus = config.appStateManager.appContext.eventBus;
      if (handednessState) sidebarConfig.handedness = handednessState;
      sidebarConfig.onToolSettingsRequest = handleSketchToolSettingsRequest;

      sidebarControls = createSidebarControls(sidebarContainer, sidebarConfig);
    }

    if (dominantContainer) {
      dominantArea = createDominantArea(dominantContainer, {
        // Pass appStateManager so DominantArea can read/update UIState (audioMuted, etc.)
        ...(config.appStateManager && {
          appStateManager: config.appStateManager,
        }),
        ...(handednessState ? { handedness: handednessState } : {}),
      } as any);
    }

    if (bottomContainer) {
      bottomArea = createBottomArea(bottomContainer, {
        projectName: "Unnamed",
        pageName: "Contact Us",
        appStateManager: config.appStateManager,
        initialShowAllLabel: "Pages",
      });
      // Subscribe BottomArea labels to selection changes
      if (config.appStateManager?.uiState) {
        const uiState = config.appStateManager.uiState;
        const state = uiState.getState();
        bottomArea.updateProject(state.current_project);
        bottomArea.updatePage(state.current_page);
      }
    }

    const initialToolOptions = paletteOptionsBySubtool.ballpoint;
    const paletteOptions =
      initialToolOptions ?? config.sketchPaletteOptions ?? {};
    paletteHost = document.createElement("div");
    sketchToolPalette = createSketchToolPalette(paletteHost, {
      colors: paletteOptions.colors,
      widths: paletteOptions.widths,
      initialColor: paletteOptions.initialColor,
      initialWidth: paletteOptions.initialWidth,
      toolLabel: initialToolOptions?.toolLabel ?? "Ballpoint",
      toolVariant: initialToolOptions?.toolVariant ?? "ballpoint",
      onColorChange: (color) => {
        config.onSketchColorChange?.(color);
      },
      onWidthChange: (width) => {
        config.onSketchWidthChange?.(width);
      },
      onClose: () => {
        closePalette();
      },
    });
    paletteHost.remove();

    if (handednessState && typeof handednessState.subscribe === "function") {
      unsubscribeHandedness = handednessState.subscribe((value) => {
        if (value === "left" || value === "right") {
          setLayoutHandedness(value);
        }
      });
    }
  };

  /**
   * Update the mode indicator display (legacy support)
   * @param _mode - Current mode to display (unused in new container layout)
   */
  const updateModeIndicator = (_mode: "edit" | "run"): void => {
    if (isDestroyed) return;
    // Could update info bar with mode if needed
  };

  /**
   * Handle transform changes (no-op for UI controls)
   * @param _transform - New transform state (unused)
   */
  const setTransform = (_transform: TransformState): void => {
    // UI controls don't transform with content
  };

  /**
   * Set layer visibility
   * @param visible - Visibility state
   */
  const applyVisibilityProfile = (profile: VisibilityProfile): void => {
    if (isDestroyed) return;

    infoHUD?.applyVisibility?.(profile.infoHUD);
    sidebarControls?.applyVisibility?.(profile.sidebar);
    dominantArea?.applyVisibility?.(profile.dominant);
    bottomArea?.applyVisibility?.(profile.bottom);

    const allowed = profile.id === "sketch";
    if (!allowed) {
      closePalette();
    }
    isPaletteAllowed = allowed;
  };

  const setVisible = (visible: boolean): void => {
    if (isDestroyed) return;
    isLayerVisible = visible;

    // Delegate to all subcomponents
    infoHUD?.setVisible(visible);
    sidebarControls?.setVisible(visible);
    dominantArea?.setVisible(visible);
    bottomArea?.setVisible(visible);

    container.style.visibility = visible ? "visible" : "hidden";
    if (!visible) {
      closePalette();
    }
  };

  /**
   * Set active tool and update UI (delegates to SidebarControls)
   * @param tool - Tool name to activate
   */
  const setActiveTool = (tool: string): void => {
    if (isDestroyed) return;
    sidebarControls?.setActiveTool(tool);
  };

  /**
   * Toggle modifier key state (delegates to SidebarControls)
   * @param modifier - Modifier key to toggle
   */
  const toggleModifier = (modifier: string): void => {
    if (isDestroyed) return;
    sidebarControls?.toggleModifier(modifier);
  };

  /**
   * Get currently active modifiers (delegates to SidebarControls)
   * @returns Array of active modifier names
   */
  const getActiveModifiers = (): string[] => {
    return sidebarControls?.getActiveModifiers() || [];
  };

  /**
   * Get current tool (delegates to SidebarControls)
   * @returns Current tool name
   */
  const getCurrentTool = (): string => {
    return sidebarControls?.getCurrentTool() || "select";
  };

  const setUndoDisabled = (disabled: boolean): void => {
    if (isDestroyed) return;
    sidebarControls?.setUndoDisabled?.(disabled);
  };

  const setRedoDisabled = (disabled: boolean): void => {
    if (isDestroyed) return;
    sidebarControls?.setRedoDisabled?.(disabled);
  };

  /**
   * Update zoom display (delegates to InfoHUD)
   * @param zoom - New zoom percentage
   */
  const updateZoomDisplay = (zoom: number): void => {
    if (isDestroyed) return;
    infoHUD?.updateZoom(zoom);
  };

  /**
   * Update selection count display (placeholder for future)
   * @param count - Number of selected elements
   */
  const updateSelectionCount = (count: number): void => {
    if (isDestroyed) return;
    // Future: Could show selection count in InfoHUD or DominantArea
    console.log(`Selection count: ${count}`);
  };

  /**
   * Show details panel (placeholder for future)
   */
  const showDetailsPanel = (): void => {
    if (isDestroyed) return;
    // Future: Could show panel in DominantArea
  };

  /**
   * Hide details panel (placeholder for future)
   */
  const hideDetailsPanel = (): void => {
    if (isDestroyed) return;
    // Future: Could hide panel in DominantArea
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;
    closePalette();

    // Destroy all subcomponents
    infoHUD?.destroy();
    sidebarControls?.destroy();
    dominantArea?.destroy();
    bottomArea?.destroy();
    sketchToolPalette?.destroy();
    sketchToolPalette = null;

    paletteHost = null;

    if (unsubscribeHandedness) {
      try {
        unsubscribeHandedness();
      } catch {}
      unsubscribeHandedness = null;
    }

    layoutRoot = null;

    container.innerHTML = "";
  };

  // Initialize controls
  initControls();

  // Return API
  return {
    updateModeIndicator,
    setTransform,
    applyVisibilityProfile,
    setVisible,
    destroy,
    // Delegated API methods to subcomponents
    setActiveTool,
    updateSelectionCount,
    updateZoomDisplay,
    toggleModifier,
    showDetailsPanel,
    hideDetailsPanel,
    getActiveModifiers,
    getCurrentTool,
    setUndoDisabled,
    setRedoDisabled,
  };
};
